import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@speakai/ui/toast";
import { useAccountUsage } from "@/features/dashboard/queries";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { useTeamMembers } from "../queries";
import type { InviteLinkSurface } from "../api";

/** Copies an invite link with a toast and tracks the copy for `surface`; `copied` stays true for 2 seconds after a copy. */
export function useCopyInviteLink(surface: InviteLinkSurface) {
  const { t } = useTranslation();
  const toast = useToast();
  const { copy: copyText, copied } = useCopyToClipboard();

  const copy = useCallback(
    async (shareUrl: string | undefined) => {
      if (!shareUrl) return;
      if (await copyText(shareUrl)) {
        toast.success(t("MEMBERS.INVITE_LINK_COPIED", "Invite link copied to clipboard"));
        trackFunnel("team", "invite_link_copied", { surface });
        pushDataLayer("Team", "Invite Link Copied", surface);
      } else {
        toast.error(t("MEMBERS.COPY_LINK_FAILED", "Failed to copy link, please copy it manually."));
      }
    },
    [copyText, t, toast, surface],
  );

  return { copy, copied };
}

/** Seats used (unfiltered member count) against the plan's seat allowance. */
export function useTeamSeats(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const { data: subscription } = useAccountUsage({ enabled });
  const { data, isLoading } = useTeamMembers({ page: 0, pageSize: 1 }, { enabled });
  const allowedMembers = subscription?.features?.collaboration?.allowUsers ?? 0;
  const totalCount = data?.totalCount ?? 0;

  return {
    totalCount,
    allowedMembers,
    isLimitReached: allowedMembers > 0 && totalCount >= allowedMembers,
    isLoading,
  };
}
